import React, {useEffect, useState} from 'react';
import {HomepageIntroContainer, IntroScrollIndicator} from './HomepageIntro.styles';
import {
    Typography,
    ArrowUp,
    Video,
    VerticalSpacer,
    Button,
    AboveFoldScrollIndicator
} from '../../elements';
import {AnimateOnViewWrap} from '../../wraps/';
import {LocalVideoOptions} from '../../../types';
import {useScroll} from '../../../shared';
import {motion, useTransform, useMotionValue} from 'framer-motion';
import {HomepageMainIntroProps} from './HomepageHeader.types';
import root from 'window-or-global';
import {useBrowserSatisfies} from 'epic-ue-shared';
import useMediaQuery from '@material-ui/core/useMediaQuery';

interface HomepageIntroProps {
    introData?: HomepageMainIntroProps;
}

export const HomepageIntro = ({introData = {}}: HomepageIntroProps): JSX.Element => {
    const {title = '', scrollLabel = '', mainVideo = '', scrollHref} = introData;
    const [isComplete, setComplete] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [scrollUp, setScrollUP] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);

    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const disableForSlowMachines = useBrowserSatisfies({
        safari: '<14',
        'internet explorer': '>8',
        chrome: '<70',
        firefox: '<70'
    });

    const {position} = useScroll();
    const scrollY = useMotionValue(0);
    scrollY.set(position.y);

    const vh = Math.max(root.innerHeight || 0);
    const heroContentArray = [0, 150, 300];

    const finalWidth = Math.floor(root.innerWidth * 0.45);
    const finalHeight = Math.floor((finalWidth / 16) * 9);

    const finalHeightRatio = finalHeight / root.innerHeight;
    const finalWidthRatio = finalWidth / root.innerWidth;

    const videoOpacity = useTransform(scrollY, [0, 900, vh], [1, 0.7, 0.4]);
    const yPosText = useTransform(scrollY, heroContentArray, [1, -50, -500]);
    const textOpacity = useTransform(scrollY, heroContentArray, [1, 0.5, 0]);
    const textX = useTransform(scrollY, heroContentArray, [-90, -160, -200]);
    const gradientRotate = useTransform(scrollY, heroContentArray, [340, 340, 340]);
    const videoOffsetY = useTransform(scrollY, [0, 500, vh], [0, 0, -(vh / 2 + 50)]);
    const videoHeight = useTransform(scrollY, [0, 250, vh], [1, 1, finalHeightRatio]);
    const videoWidth = useTransform(scrollY, [0, 250, vh], [1, 1, finalWidthRatio]);

    const mediaOptions: LocalVideoOptions = {
        content: [mainVideo]
    };

    useEffect(() => {
        if (position.y) {
            setHasScrolled(true);

            if (hasLoaded && !scrollUp) {
                if ('scrollRestoration' in history) {
                    history.scrollRestoration = 'manual';
                }
                setScrollUP(true);
            }

            if (position.y >= vh) {
                setComplete(true);
            } else {
                setComplete(false);
            }
        }
        setHasLoaded(true);
    }, [
        position,
        setHasLoaded,
        setHasScrolled,
        setScrollUP,
        setComplete,
        scrollUp,
        hasLoaded,
        hasScrolled
    ]);

    const jumpDown = (e) => {
        if (e) {
            root.scrollTo({
                top: vh + 600,
                behavior: 'smooth'
            });
        }
    };

    //Only scroll if href is not linking to a new page
    const needScroll = !scrollHref || scrollHref?.startsWith('#');

    return (
        <HomepageIntroContainer
            className={`${hasLoaded ? 'hasLoaded' : ''} ${isComplete ? 'isComplete' : ''}`}>
            {!disableForSlowMachines && (
                <motion.div
                    className="gradient"
                    style={{
                        opacity: hasScrolled ? textOpacity : 1,
                        translateX: hasScrolled ? textX : 0,
                        rotate: hasScrolled ? gradientRotate : 0
                    }}
                />
            )}

            <motion.div
                className="video-container"
                style={{
                    translateY: hasScrolled ? videoOffsetY : 0,
                    scaleY: hasScrolled && !reducedMotion ? videoHeight : 1,
                    scaleX: hasScrolled && !reducedMotion ? videoWidth : 1,
                    opacity: hasScrolled ? videoOpacity : 1
                }}>
                <div className="video-ratio">
                    <div className="video-size" id="ue-intro-video">
                        <Video mediaOptions={mediaOptions} gradient={true} />
                    </div>
                </div>
            </motion.div>

            <div className="content">
                <motion.div
                    style={{
                        y: hasScrolled ? yPosText : 0,
                        opacity: hasScrolled ? textOpacity : 1
                    }}>
                    <AnimateOnViewWrap delay={0.85} translateY={-20}>
                        <Typography variant="h1">{title || ''}</Typography>
                        <VerticalSpacer variant="feature" />

                        <IntroScrollIndicator onClick={needScroll ? jumpDown : undefined}>
                            {scrollHref && scrollLabel ? (
                                <>
                                    <Button
                                        className="homepageHeroCTA"
                                        type="secondary"
                                        href={scrollHref}>
                                        {scrollLabel}
                                    </Button>
                                    <VerticalSpacer variant="feature" />
                                    
                                </>
                            ) : (
                                <>
                                    <ArrowUp />
                                    <Typography variant="h4" big={true}>
                                        {scrollLabel}
                                    </Typography>
                                </>
                            )}
                        </IntroScrollIndicator>
                        {scrollHref && scrollLabel && <AboveFoldScrollIndicator forceSmall translateY={0} alignLeft={true} />}
                    </AnimateOnViewWrap>
                </motion.div>
            </div>
        </HomepageIntroContainer>
    );
};
